import ClearIcon from '@mui/icons-material/Clear'
import { IconButton, InputAdornment, Stack, useTheme } from '@mui/material'
import { DatePicker as MuiDatePicker } from '@mui/x-date-pickers/DatePicker'
import type { PickersTextFieldProps } from '@mui/x-date-pickers/PickersTextField'
import { PickerValue } from '@mui/x-date-pickers/internals'
import { FieldProps, getIn, useFormikContext } from 'formik'
import { FocusEvent } from 'react'
import { ExtendedTheme } from '../../../../theme'
import { createErrorHandler } from './errorHandler'

export interface DatePickerProps
  extends FieldProps,
    Omit<
      PickersTextFieldProps<'standard'>,
      'name' | 'error' | 'InputProps' | 'onChange' | 'variant' | 'defaultValue' | 'value'
    > {
  inlineClearButton?: boolean
  ciboSourced?: boolean
  defaultValue?: PickerValue
  value?: PickerValue
  onChange: (date: PickerValue | null) => void
}

export function DatePicker(props: DatePickerProps) {
  const {
    field: { onChange: _onChange, value, ...field },
    form,
    inlineClearButton,
    disabled,
    ciboSourced,
    label,
    onChange,
    onError,
    ...rest
  } = props
  const { isSubmitting, setFieldError } = form
  const { setFieldValue, setFieldTouched, touched, errors } = useFormikContext<any>()
  const fieldError = getIn(errors, field.name)
  const theme = useTheme<ExtendedTheme>()
  const showError = getIn(touched, field.name) && !!fieldError

  const textFieldProps: Partial<PickersTextFieldProps> = {
    error: showError as any,
    helperText: showError ? fieldError : null,
    label,
    size: 'small',
    sx: { background: ciboSourced ? theme.palette.ciboSensed : undefined },
    onBlur: (event: FocusEvent<HTMLDivElement, Element>) => {
      setFieldTouched(field.name, true, true)
    },
    endAdornment: (
      <Stack direction="row" alignItems="center">
        <InputAdornment
          position="end"
          sx={{
            visibility: inlineClearButton ? 'visible' : 'hidden',
            transition: 'opacity 0.2s',
            opacity: 0,
            '.Mui-focused &, &:hover &, .Mui-focused, &:hover': {
              opacity: 1,
            },
            // Show on parent hover/focus
            '.MuiInputBase-root:hover &, .MuiInputBase-root.Mui-focused &': {
              opacity: 1,
            },
          }}
        >
          <IconButton
            size="small"
            onClick={() => setFieldValue(field.name, null, true)}
            sx={{ marginRight: '-14px' }}
            tabIndex={-1}
          >
            <ClearIcon sx={{ fontSize: '1.25rem' }} />
          </IconButton>
        </InputAdornment>

        {/* @ts-ignore -- endAdornment is a valid possible option but TS disagrees */}
        {props.InputProps?.endAdornment || props.slotProps?.input?.endAdornment}
      </Stack>
    ),
  }

  return (
    <MuiDatePicker
      slotProps={{ textField: textFieldProps }}
      disabled={disabled ?? isSubmitting}
      onError={createErrorHandler(fieldError, field.name, setFieldError)}
      value={value ? new Date(value) : null}
      {...field}
      onChange={
        onChange ??
        ((date: PickerValue | undefined) => {
          // Do not switch this order, otherwise you might cause a race condition
          // See https://github.com/formium/formik/issues/2083#issuecomment-884831583
          setFieldTouched(field.name, true, false)
          setFieldValue(field.name, date, true)
        })
      }
      {...rest}
    />
  )
}

DatePicker.displayName = 'DatePickerField'
